import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { COMPLETION_TYPE_SIGNUP_QUERY } from '../graphql/queries';

interface SparkContent {
  headerText: string;
  formLabelText: string;
  buttonText: string;
}

interface SparkContextType {
  content: SparkContent;
  isLoading: boolean;
  hasSparkId: boolean;
}

const SparkContext = createContext<SparkContextType | undefined>(undefined);

function SparkProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('billing');
  const [searchParams] = useSearchParams();
  const sparkId = searchParams.get('spark_id');
  const [isLoading, setIsLoading] = useState(!!sparkId);

  // Query completion type for dynamic signup content
  const [getCompletionType, { data: sparkData }] = useLazyQuery(COMPLETION_TYPE_SIGNUP_QUERY, {
    errorPolicy: 'ignore', // Don't show errors for invalid spark IDs
    onCompleted: () => setIsLoading(false),
    onError: () => setIsLoading(false),
  });

  // Fetch spark data when spark_id is present
  useEffect(() => {
    if (sparkId) {
      setIsLoading(true);
      getCompletionType({ variables: { id: sparkId } });
    } else {
      setIsLoading(false);
    }
  }, [sparkId, getCompletionType]);

  // Get dynamic content with fallbacks
  const content: SparkContent = useMemo(() => {
    const spark = sparkData?.sparkMarketing;

    return {
      headerText: spark?.signupHeaderText || t('spark.default-header'),
      formLabelText: spark?.signupFormLabelText || t('spark.default-form-label'),
      buttonText: spark?.signupButtonText || t('spark.default-button'),
    };
  }, [sparkData, t]);

  const value: SparkContextType = useMemo(
    () => ({
      content,
      isLoading,
      hasSparkId: !!sparkId,
    }),
    [content, isLoading, sparkId]
  );

  return <SparkContext.Provider value={value}>{children}</SparkContext.Provider>;
}

function useSpark(): SparkContextType {
  const context = useContext(SparkContext);
  if (context === undefined) {
    throw new Error('useSpark must be used within a SparkProvider');
  }
  return context;
}

export { SparkProvider, useSpark };
